<template>
    <ion-page id="main-content">
        <div class="loading-container" v-if="!show404">
            <img
                v-if="loaderIconSrc"
                :src="loaderIconSrc"
                class="loader-app-icon"
                alt=""
                @error="onLoaderIconError"
            />
            <ion-progress-bar :value="progress"></ion-progress-bar>
            <div class="loading-slug"> {{ loadingSlug }} </div>
        </div>
        <div v-else class="error-container">
            <h1>404</h1>
            <p>The resource you requested does not exist or is no longer available.</p>
        </div>
    </ion-page>
</template>

<script lang="ts">
import { IonPage, IonProgressBar } from "@ionic/vue";
import {store} from "@/store";
import {computed, ref, onMounted, watch} from "vue";
import {readImageDataUriFromDataDirectory, logInfo} from "@/utilities/Filesystem";

const ASSET_APP_ICON = "/assets/img/appIcon.png";
const ASSET_GLOBAL_APP_ICON = "/assets/img/globalAppIcon.png";

export default {
    name: "MainLoader",
    components: {
        IonPage,
        IonProgressBar
    },
    setup() {
        const iconFallbackRef = ref("");
        const loadedIconFromDataRef = ref("");
        const assetFailedRef = ref(false);
        const isPwa = () => store.getters.isPwa();
        // PWA: S3 icon from Directory.Data if present, else fall back to globalAppIcon.png in assets. Non-PWA: data dir then asset then global.
        const loaderIconSrc = computed(() => {
            if (loadedIconFromDataRef.value) return loadedIconFromDataRef.value;
            if (assetFailedRef.value) return iconFallbackRef.value || "";
            if (isPwa()) return iconFallbackRef.value || ASSET_GLOBAL_APP_ICON; // PWA: fall back to global icon when S3 icon not available
            return ASSET_APP_ICON || iconFallbackRef.value;
        });
        const tryLoadIconFromData = async () => {
            try {
                const dataUri = await readImageDataUriFromDataDirectory("appIcon.png");
                if (dataUri) {
                    loadedIconFromDataRef.value = dataUri;
                    logInfo("[MainLoader] Directory.Data: got dataUri", 0);
                }
            } catch (e) {
                logInfo("[MainLoader] Directory.Data: read error " + (e instanceof Error ? e.message : String(e)), 0);
            }
        };
        onMounted(() => {
            setTimeout(() => tryLoadIconFromData(), 100);
            if (isPwa()) {
                watch(() => store.state.loadingValue, (val) => { if (val >= 0.4 && !loadedIconFromDataRef.value) tryLoadIconFromData(); }, { immediate: true });
                // Icon is written after getMinRequiredDataForAppLoad + downloadAndSaveAppIcon in main.ts; poll until it appears
                const pollMs = 300;
                const maxAttempts = 20;
                let attempts = 0;
                const poll = () => {
                    if (loadedIconFromDataRef.value || attempts >= maxAttempts) return;
                    attempts++;
                    tryLoadIconFromData().then(() => {
                        if (!loadedIconFromDataRef.value) setTimeout(poll, pollMs);
                    });
                };
                setTimeout(poll, 500);
            }
        });
        async function onLoaderIconError() {
            if (loadedIconFromDataRef.value) {
                logInfo("[MainLoader] data dir icon failed to load, clearing to fall back", 0);
                loadedIconFromDataRef.value = "";
                return;
            }
            if (iconFallbackRef.value === ASSET_GLOBAL_APP_ICON) {
                iconFallbackRef.value = "";
                return;
            }
            assetFailedRef.value = true;
            console.log("[MainLoader] asset icon error, trying Directory.Data");
            try {
                const dataUri = await readImageDataUriFromDataDirectory("appIcon.png");
                if (dataUri) {
                    loadedIconFromDataRef.value = dataUri;
                    logInfo("[MainLoader] Directory.Data: got dataUri on error", 0);
                    return;
                }
            } catch (e) {
                logInfo("[MainLoader] Directory.Data error handler: " + (e instanceof Error ? e.message : String(e)), 0);
            }
            logInfo("[MainLoader] falling back to global icon (or hide if missing)", 0);
            iconFallbackRef.value = ASSET_GLOBAL_APP_ICON;
        }
        // PWA: show 404 when invalid URL (e.g. wrong subdomain like discoverystudy.patternsites.com).
        // Mobile: never show 404 in loader so a 404 from an S3 init request doesn't flash at startup.
        const show404 = computed(() => store.getters.isPwa() && store.getters.getIsInvalidUrl());
        return {
            progress: computed(() => store.getters.getLoadingValue()),
            loadingSlug: computed(() => store.state.loadingSlug),
            show404,
            loaderIconSrc,
            onLoaderIconError
        }
    }
}
</script>

<style scoped>
#main-content {
    display: flex;
    align-items: center;
    justify-content: center;
}
div.loading-container {
    position:relative;
    width: 80%;
    display: flex;
    flex-direction: column;
    align-items: center;
}
.loader-app-icon {
    width: 120px;
    height: 120px;
    object-fit: contain;
    margin-bottom: 16px;
}

div {
    position: absolute;
    top:0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
}
ion-progress-bar {
    width: 100%;
}
.loading-slug {
    margin-top: 10px;
}

.error-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
.error-container h1 {
    font-size: 120px;
    font-weight: 400;
    color: #333;
}
.error-container p {
    font-size: 20px;
}
</style>
